<script setup lang="ts">
// The living code map: a sigma.js (WebGL) view over a code-graph snapshot.
// Semantic zoom only shows/hides levels — positions come from the snapshot,
// so nothing jumps. A filter (lib/graph/filter.ts) only hides, and folds events
// that share their ends into one counted node at their centre. An overlay
// canvas draws what WebGL nodes can't: data particles along flow edges, the
// agent's trail and the "now" pulse.
import { onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import Graph from "graphology";
import Sigma from "sigma";
import type { GraphNode, GraphSnapshot } from "@/api/types";
import type { NodeActivity } from "@/lib/graph/mapping";
import { GROUP_ENDS, GROUP_PREFIX, shortLabel, type EventGroup } from "@/lib/graph/filter";
import { DETAIL_LABELS } from "@/lib/mapModes";
import { alpha, EDGE_COLOR, FLOW_KINDS, G, nodeColor, solid } from "@/lib/graph/theme";
import { useTheme } from "@/lib/theme";

export type MapMode = "structure" | "activity" | "flow" | "tickets" | "hotspots" | "blast";

const props = withDefaults(defineProps<{
  snapshot: GraphSnapshot | null;
  mode?: MapMode;
  activity?: Map<string, NodeActivity> | null;
  trail?: string[];
  blast?: Map<string, number> | null;
  footprint?: Map<string, { reads: number; edits: number }> | null;
  selected?: string | null;
  focusIds?: string[];
  compact?: boolean;
  insetRight?: number; // px covered by a panel on the right; focusing frames the rest
  run?: boolean;        // drawing one run: edges only around what the agents touched
  extraNodes?: GraphNode[]; // ephemeral nodes added live, without a remount
  visible?: Set<string> | null; // what the filter lets through (null: everything)
  groups?: EventGroup[];        // folded events, drawn as one node each
}>(), { insetRight: 0, run: false, extraNodes: () => [], mode: "structure", activity: null, trail: () => [], blast: null, footprint: null, selected: null, focusIds: () => [], compact: false, visible: null, groups: () => [] });

const emit = defineEmits<{ select: [id: string | null]; hover: [id: string | null]; detail: [band: number] }>();

const host = ref<HTMLDivElement | null>(null);
const overlay = ref<HTMLCanvasElement | null>(null);
const zoomLevel = ref(1);
const webglError = ref("");
const renderer = shallowRef<Sigma | null>(null);
let graph: Graph | null = null;
let hovered: string | null = null;
let hoverNeighbors = new Set<string>();
let hoverLinked = new Set<string>(); // neighbours by a real link (not containment): the ones hover names
let selectedNeighbors = new Set<string>();
let inDegree = new Map<string, number>();
// What the overlay draws from, kept with the graph instead of scanned every frame.
let packages: [string, string][] = [];
let flowEdges: [string, Record<string, unknown> & { kind: string }, string, string][] = [];
const PARTICLE_GLOW_MAX = 300;  // more flow edges than this: plain particles, one per edge
const PARTICLE_EDGES_MAX = 1500;

function indexOverlay(g: Graph) {
  packages = [];
  g.forEachNode((n, a) => { if (a.kind === "package" && a.repo) packages.push([n, a.repo as string]); });
  flowEdges = [];
  g.forEachEdge((e, a, s, t) => { if (FLOW_KINDS.has(a.kind) || a.kind === "agg_flow") flowEdges.push([e, a as Record<string, unknown> & { kind: string }, s, t]); });
}
let raf = 0;
let resizeObs: ResizeObserver | null = null;

// Receded nodes keep their names, faintly, where the label grid has room: the
// context stays readable while the subject stands out.

// Hovering a node names its neighbours, up to this many (beyond, the label grid decides).
const HOVER_LABELS = 40;

// ── drag to pull ──
// Grab a node and its connections come with it on springs: its own children
// almost rigidly, direct connections at about half the motion, second-hop ones
// loosely. Everything else recedes and a badge counts what is being pulled. On
// release they spring back (Shift-release keeps the new positions).
interface Body { x0: number; y0: number; x: number; y: number; vx: number; vy: number; w: number }
interface Pull {
  node: string; bodies: Map<string, Body>; ring1: Set<string>; ring2: Set<string>;
  dx: number; dy: number; held: boolean; direct: number; indirect: number;
}
const PULL_W = { child: 0.94, direct: 0.55, indirect: 0.22 };
const PULL_MAX = { direct: 400, indirect: 1200 };
const SPRING_K = 0.16, SPRING_DAMP = 0.74; // per frame: a little overshoot, settles in ~0.6 s
let pull: Pull | null = null;
let pullRaf = 0;
let justDragged = false;
const sides: Record<string, string> = {};

// Semantic zoom thresholds on the sigma camera ratio (bigger = further out):
// four bands of detail, repositories → packages → modules → symbols.
const SHOW_PACKAGES = 1.4;
const SHOW_MODULES = 0.55;
const SHOW_SYMBOLS = 0.2;
// The camera ratio each detail stop flies to (inside its band).
const DETAIL_RATIO = [2.2, 1, 0.36, 0.12];

function band(ratio: number): number {
  return ratio > SHOW_PACKAGES ? 0 : ratio > SHOW_MODULES ? 1 : ratio > SHOW_SYMBOLS ? 2 : 3;
}

function levelVisible(level: number, kind: string, ratio: number): boolean {
  if (kind === "symbol" || kind === "lg_tool") return ratio <= SHOW_SYMBOLS;
  if (kind === "module") return ratio <= SHOW_MODULES;
  if (kind === "package") return ratio <= SHOW_PACKAGES;
  return level <= 3; // repos and domain nodes are the skeleton at every zoom
}

// Roll-ups stand in for hidden detail: package-level ones while packages are the
// finest thing shown, repo-level ones when only repositories are.
function aggShown(data: Record<string, unknown>, ratio: number): boolean {
  return data.aggLevel === "repo" ? ratio > SHOW_PACKAGES : ratio > SHOW_MODULES && ratio <= SHOW_PACKAGES;
}

// What the filter lets through; group nodes and live (ephemeral) nodes always pass.
function passes(id: string): boolean {
  return !props.visible || props.visible.has(id) || id.startsWith(GROUP_PREFIX) || ephemeral.has(id);
}

// Display size per kind, whatever scale the indexer used: each kind gets a
// pixel range and its nodes spread across it by relative (sqrt) size, so a
// 60-unit package and a 2-unit symbol both land somewhere readable.
const SIZE_RANGE: Record<string, [number, number]> = {
  repo: [13, 13], package: [5, 12], module: [2.4, 6], symbol: [1.2, 3.2],
  contract_event: [5.5, 7], bus_event: [4, 5.2], aws_resource: [4, 5.2], http_route: [3.6, 4.6],
  lg_agent: [5, 7.5], lg_tool: [2.2, 3],
};

function sizer(snapshot: GraphSnapshot): (n: GraphSnapshot["nodes"][number]) => number {
  const span = new Map<string, [number, number]>();
  for (const n of snapshot.nodes) {
    const v = Math.sqrt(Math.max(0, n.size ?? 0));
    const cur = span.get(n.kind);
    span.set(n.kind, cur ? [Math.min(cur[0], v), Math.max(cur[1], v)] : [v, v]);
  }
  return (n) => {
    const [lo, hi] = SIZE_RANGE[n.kind] ?? [2, 4];
    const [a, b] = span.get(n.kind) ?? [0, 0];
    const t = b > a ? (Math.sqrt(Math.max(0, n.size ?? 0)) - a) / (b - a) : 0.5;
    return lo + (hi - lo) * t;
  };
}

let sizeOf: (n: GraphNode) => number = () => 3;
const ephemeral = new Set<string>();

function addExtraNodes(nodes: GraphNode[]): boolean {
  if (!graph) return false;
  let added = false;
  for (const n of nodes) {
    if (graph.hasNode(n.id)) continue;
    const base = sizeOf(n);
    graph.addNode(n.id, {
      x: n.x, y: n.y, size: base, baseSize: base, label: n.label, kind: n.kind, level: n.level,
      color: G.ephemeral, baseColor: G.ephemeral, repo: n.repo ?? null,
    });
    ephemeral.add(n.id);
    added = true;
  }
  return added;
}

function build(snapshot: GraphSnapshot): Graph {
  const g = new Graph({ multi: false, type: "directed", allowSelfLoops: false });
  parentOf.clear();
  for (const n of snapshot.nodes) if (n.parent) parentOf.set(n.id, n.parent);
  snapshot.repos.forEach((r, i) => { sides[r.repo] = r.side ?? (i === 0 ? "backend" : "frontend"); });
  const size = sizer(snapshot);
  sizeOf = size;
  ephemeral.clear();
  for (const n of snapshot.nodes) {
    if (g.hasNode(n.id)) continue;
    const base = size(n);
    g.addNode(n.id, {
      x: n.x, y: n.y, size: base, baseSize: base, label: n.label, kind: n.kind, level: n.level,
      color: nodeColor(n, sides), baseColor: nodeColor(n, sides), repo: n.repo ?? null,
    });
  }
  for (const e of snapshot.edges) {
    if (!g.hasNode(e.source) || !g.hasNode(e.target) || e.source === e.target) continue;
    if (g.hasDirectedEdge(e.source, e.target)) continue;
    g.addDirectedEdgeWithKey(e.id, e.source, e.target, {
      kind: e.kind, confidence: e.confidence ?? "extracted",
      color: EDGE_COLOR[e.kind] ?? G.fallbackEdge,
      size: FLOW_KINDS.has(e.kind) ? 2 : e.kind === "contains" ? 0.5 : 1,
    });
  }
  inDegree = new Map(g.nodes().map((n) => [n, g.inDegree(n)]));
  applyGroups(g);
  rebuildAggregates(g);
  indexOverlay(g);
  return g;
}

// Folded nodes: one at the members' centre, joined once to each module, package
// or repo at its ends (the members stay in the graph, hidden by the filter).
function applyGroups(g: Graph) {
  g.forEachNode((n) => { if (n.startsWith(GROUP_PREFIX)) toDrop.push(n); });
  toDrop.splice(0).forEach((n) => g.dropNode(n));
  for (const grp of props.groups ?? []) {
    const size = 6 + Math.min(6, Math.log2(grp.members.length) * 1.5);
    const color = nodeColor({ kind: grp.kind, repo: null }, sides);
    g.addNode(grp.id, { x: grp.x, y: grp.y, size, baseSize: size, label: grp.label, kind: "event_group", memberKind: grp.kind,
      level: 3, color, baseColor: color, repo: null });
    const join = GROUP_ENDS[grp.kind] ?? GROUP_ENDS.contract_event;
    for (const [ends, kind] of [[grp.producers, join.produce], [grp.consumers, join.consume]] as const) {
      for (const m of ends) {
        if (!g.hasNode(m) || g.hasDirectedEdge(m, grp.id)) continue;
        g.addDirectedEdgeWithKey(`${grp.id}<-${m}`, m, grp.id, { kind, confidence: "extracted", folded: grp.members.length,
          color: EDGE_COLOR[kind] ?? G.fallbackEdge, size: 2 + Math.min(2, Math.log2(grp.members.length) / 2) });
      }
    }
  }
}

// Zoomed out, module/symbol edges are hidden with their nodes. Roll them up to
// package → package (and package → domain node) edges weighted by count, so the
// overview still shows how the pieces depend on and talk to each other.
function packageOf(g: Graph, node: string): string {
  let cur = node;
  for (let i = 0; i < 4; i++) {
    const kind = g.getNodeAttribute(cur, "kind");
    if (kind === "package" || kind === "repo") return cur;
    const parent = parentOf.get(cur);
    if (!parent || !g.hasNode(parent)) return cur; // domain nodes stand on their own
    cur = parent;
  }
  return cur;
}

const parentOf = new Map<string, string>();
const toDrop: string[] = []; // scratch for removals (graphology can't drop while iterating)

function repoOfNode(g: Graph, node: string): string {
  let cur = node;
  for (let i = 0; i < 5; i++) {
    if (g.getNodeAttribute(cur, "kind") === "repo") return cur;
    const parent = parentOf.get(cur);
    if (!parent || !g.hasNode(parent)) return cur; // domain nodes stand on their own
    cur = parent;
  }
  return cur;
}

// Rebuilt whenever the filter changes, from the original edges whose ends it
// lets through: a roll-up never stands for something the filter hid.
function rebuildAggregates(g: Graph) {
  g.forEachEdge((e, a) => { if (a.agg) toDrop.push(e); });
  toDrop.splice(0).forEach((e) => g.dropEdge(e));
  const agg = new Map<string, { s: string; t: string; w: number; flow: boolean; level: "package" | "repo" }>();
  g.forEachEdge((_e, a, src, tgt) => {
    if (a.kind === "contains" || !passes(src) || !passes(tgt)) return;
    for (const level of ["package", "repo"] as const) {
      const s = level === "package" ? packageOf(g, src) : repoOfNode(g, src);
      const t = level === "package" ? packageOf(g, tgt) : repoOfNode(g, tgt);
      if (s === t || s === src && t === tgt) continue; // already an edge at this level
      const key = `${level}:${s}→${t}`;
      const cur = agg.get(key) ?? { s, t, w: 0, flow: false, level };
      cur.w += Number(a.folded ?? 1);
      cur.flow ||= FLOW_KINDS.has(a.kind);
      agg.set(key, cur);
    }
  });
  for (const [key, a] of agg) {
    if (g.hasDirectedEdge(a.s, a.t)) continue;
    g.addDirectedEdgeWithKey(`agg:${key}`, a.s, a.t, {
      kind: a.flow ? "agg_flow" : "agg", agg: true, aggLevel: a.level, weight: a.w,
      color: a.flow ? G.agg.flow
        : sides[g.getNodeAttribute(a.s, "repo") as string] === "frontend" ? G.agg.frontend : G.agg.backend,
      size: Math.min(5, 1 + Math.log2(1 + a.w)),
    });
  }
}

// The camera's zoom, read once per frame by the reducers (they run for every node and edge).
let ratioNow = 1;

function nodeReducer(node: string, data: Record<string, unknown>): Record<string, unknown> {
  const r = ratioNow;
  const kind = data.kind as string;
  const res: Record<string, unknown> = { ...data };
  const act = props.activity?.get(node);
  const fp = props.footprint?.get(node);
  const dist = props.blast?.get(node);
  const touched = !!act || !!fp || dist !== undefined || node === props.selected || props.trail.includes(node);
  // The filter decides first, and nothing below brings a filtered node back
  // (touched nodes always pass: the API's filter view keeps them too).
  if (!touched && !passes(node)) { res.hidden = true; return res; }
  // Touched nodes are always visible, whatever the zoom: the story beats the zoom level.
  if (!touched && !levelVisible(data.level as number, kind, r)) res.hidden = true;
  // Long names share the label grid as a short form; the inspector has the whole name.
  if (node !== props.selected && node !== hovered && typeof data.label === "string" && kind !== "repo") res.label = shortLabel(data.label);

  if (props.mode === "hotspots") {
    const d = inDegree.get(node) ?? 0;
    res.size = (data.baseSize as number) * (1 + Math.min(1.6, Math.log2(1 + d) / 3.5));
  }
  if (props.mode === "flow" && !["contract_event", "bus_event", "aws_resource", "http_route", "lg_agent", "event_group", "repo", "package"].includes(kind)) {
    res.color = G.flowDim;
  }
  if (dist !== undefined) {
    res.color = G.activity.blast[Math.min(dist, G.activity.blast.length - 1)];
    res.size = (data.baseSize as number) * (dist === 0 ? 2.2 : 1.6);
    // The changed nodes always name themselves; dependents compete for the label grid,
    // so dense reaches stay legible (select one to name its neighbours).
    res.forceLabel = dist === 0;
    res.zIndex = 3;
  }
  if (!fp && !act && dist === undefined && (props.footprint?.size || props.blast?.size) && kind !== "repo" && node !== props.selected) {
    // A footprint (ticket or activity) is the story: everything else recedes.
    res.color = kind === "package" ? G.dimPackage : G.dim;
    res.labelColor = G.mutedLabel;
    res.zIndex = 0;
  }
  if (fp) {
    res.color = fp.edits ? G.activity.edit : G.activity.ticket;
    res.size = (data.baseSize as number) * (1.6 + Math.min(2, (fp.reads + fp.edits * 3) / 6));
    res.forceLabel = fp.edits > 0;
    res.zIndex = 3;
  }
  if (act) {
    res.color = act.edits ? G.activity.edit : act.lastKind === "probe" ? G.activity.probe : G.activity.read;
    res.size = (data.baseSize as number) * (1.8 + Math.min(2.4, (act.reads + act.probes + act.edits * 3) / 4));
    res.forceLabel = act.edits > 0 || act.probes > 0 || act.reads > 1 || props.trail[props.trail.length - 1] === node;
    res.zIndex = 4;
  }
  if (pull) {
    if (node === pull.node) { res.highlighted = true; res.forceLabel = true; res.zIndex = 7; res.hidden = false; }
    else if (pull.ring1.has(node)) { res.forceLabel = true; res.zIndex = 5; res.hidden = false; }
    else if (pull.ring2.has(node)) { res.zIndex = 4; }
    else { res.color = G.dim; res.labelColor = G.mutedLabel; res.zIndex = 0; }
    return res;
  }
  if (hovered) {
    if (node === hovered) { res.forceLabel = true; res.zIndex = 5; res.highlighted = true; }
    else if (!hoverNeighbors.has(node)) { res.color = G.dim; res.labelColor = G.mutedLabel; res.zIndex = 0; }
    // What it connects to is the point of hovering: name its neighbours.
    else if (hoverLinked.has(node) && hoverLinked.size <= HOVER_LABELS) { res.forceLabel = true; res.zIndex = Math.max(Number(res.zIndex ?? 0), 4); }
  }
  if (node === props.selected) { res.highlighted = true; res.forceLabel = true; res.zIndex = 6; }
  else if (props.selected && !hovered && !touched) {
    // A selection is a question about one node: its neighbours answer, the rest recede.
    if (selectedNeighbors.has(node)) { res.forceLabel = selectedNeighbors.size <= HOVER_LABELS; res.zIndex = 4; }
    else { res.color = G.dim; res.labelColor = G.mutedLabel; res.zIndex = 0; }
  }
  return res;
}

// Sigma's highlight draws a white box behind the label, unreadable under the
// dark theme's light labels: the map's own badge colours instead, in both themes.
function drawHover(ctx: CanvasRenderingContext2D, data: { x: number; y: number; size: number; label?: string | null },
                   settings: { labelSize: number; labelFont: string; labelWeight: string }) {
  const size = settings.labelSize;
  ctx.font = `${settings.labelWeight} ${size}px ${settings.labelFont}`;
  const label = typeof data.label === "string" ? data.label : "";
  const pad = 4, h = size + pad * 2;
  ctx.fillStyle = G.overlay.badgeBg;
  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = 8;
  ctx.beginPath();
  if (label) ctx.roundRect(data.x - data.size - pad, data.y - h / 2, data.size * 2 + pad * 3 + ctx.measureText(label).width, h, h / 2);
  else ctx.arc(data.x, data.y, data.size + pad, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  if (label) {
    ctx.fillStyle = G.overlay.badgeText;
    ctx.fillText(label, data.x + data.size + pad, data.y + size / 3);
  }
}

function edgeReducer(edge: string, data: Record<string, unknown>): Record<string, unknown> {
  const res = edgeStyle(edge, data);
  if (typeof res.color === "string") res.color = solid(res.color);
  return res;
}

function edgeStyle(edge: string, data: Record<string, unknown>): Record<string, unknown> {
  const g = graph!;
  const res: Record<string, unknown> = { ...data };
  const [s, t] = g.extremities(edge);
  const kind = data.kind as string;
  if (pull) {
    const inPull = (n: string) => n === pull!.node || pull!.ring1.has(n) || pull!.ring2.has(n);
    if (kind === "contains" || !inPull(s) || !inPull(t)) { res.hidden = true; return res; }
    const touchesDragged = s === pull.node || t === pull.node;
    res.hidden = false;
    res.color = alpha(EDGE_COLOR[kind] ?? G.fallbackEdge, touchesDragged ? 0.95 : 0.35);
    res.size = touchesDragged ? 1.8 : 0.8;
    return res;
  }
  if (hovered) {
    if (s !== hovered && t !== hovered) res.hidden = true;
    else { res.color = alpha(EDGE_COLOR[kind] ?? G.fallbackEdge, 0.95); res.size = 1.6; }
    return res;
  }
  const zoomedOutNow = ratioNow > SHOW_MODULES;
  if (props.selected && graph?.hasNode(props.selected) && !(props.blast && props.blast.size)) {
    if (data.agg && !aggShown(data, ratioNow)) { res.hidden = true; return res; }
    if ((s === props.selected || t === props.selected) && !(data.agg === undefined && zoomedOutNow && g.getNodeAttribute(s === props.selected ? t : s, "level") > 3)) {
      res.color = alpha(EDGE_COLOR[kind] ?? G.fallbackEdge, 0.9);
      res.size = 1.6;
      res.zIndex = 2;
      return res;
    }
    res.hidden = true;
    return res;
  }
  if (props.footprint?.size && !props.run) {
    // Ticket / activity footprint: edges only at the files agents touched, plus faint roll-ups.
    if (data.agg) {
      res.hidden = !aggShown(data, ratioNow);
      res.color = (data.color as string).replace(/[\d.]+\)$/, "0.18)");
      return res;
    }
    const fp = props.footprint;
    if (kind === "contains" || !(fp.has(s) || fp.has(t))) { res.hidden = true; return res; }
    res.color = alpha(EDGE_COLOR[kind] ?? G.fallbackEdge, 0.5);
    return res;
  }
  if (props.run) {
    // On a run, the story is the agent's path: only edges at nodes it touched
    // (what it saw around them), plus faint roll-ups for the overview.
    if (data.agg) {
      res.hidden = !aggShown(data, ratioNow);
      res.color = (data.color as string).replace(/[\d.]+\)$/, "0.22)");
      return res;
    }
    const touched = (id: string) => !!props.activity?.get(id) || props.trail.includes(id);
    if (kind === "contains" || !(touched(s) || touched(t))) { res.hidden = true; return res; }
    res.color = alpha(EDGE_COLOR[kind] ?? G.fallbackEdge, 0.55);
    res.size = 1.2;
    return res;
  }
  if (data.agg) {
    // Roll-ups stand in for the detail only while the detail is hidden.
    res.hidden = !aggShown(data, ratioNow) || (props.mode === "flow" && kind !== "agg_flow") || !!(props.blast && props.blast.size);
    return res;
  }
  if (props.mode === "flow") {
    if (!FLOW_KINDS.has(kind)) res.hidden = true;
    return res;
  }
  // Outside Data flow, runtime edges are context, not the story: thin and quiet.
  if (FLOW_KINDS.has(kind)) {
    res.size = 1;
    res.color = (data.color as string).replace(/[\d.]+\)$/, "0.35)");
  }
  if (kind === "contains" && !props.compact) res.hidden = ratioNow > SHOW_MODULES;
  if (props.compact && (kind === "contains" || kind === "calls")) res.hidden = true;
  if ((props.blast && props.blast.size) && !(props.blast.has(s) && props.blast.has(t))) res.hidden = true;
  if (data.confidence === "ambiguous") res.color = G.ambiguous;
  return res;
}

// ── overlay: particles, trail, pulse ──
function viewport(node: string): { x: number; y: number; size: number } | null {
  const r = renderer.value;
  if (!r || !graph?.hasNode(node)) return null;
  const d = r.getNodeDisplayData(node);
  if (!d || d.hidden) return null;
  const p = r.framedGraphToViewport({ x: d.x, y: d.y });
  return { x: p.x, y: p.y, size: d.size };
}

function drawOverlay(now: number) {
  const c = overlay.value;
  const r = renderer.value;
  if (!c || !r || !graph) { raf = requestAnimationFrame(drawOverlay); return; }
  const ctx = c.getContext("2d")!;
  const dpr = window.devicePixelRatio || 1;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, c.width, c.height);

  // Repo regions: a soft halo behind each repo's packages, labelled.
  const regions = new Map<string, { xs: number[]; ys: number[] }>();
  for (const [n, repo] of packages) {
    const p = viewport(n);
    if (!p) continue;
    const reg = regions.get(repo) ?? { xs: [], ys: [] };
    reg.xs.push(p.x); reg.ys.push(p.y);
    regions.set(repo, reg);
  }
  for (const [repo, reg] of regions) {
    const cx = reg.xs.reduce((s, v) => s + v, 0) / reg.xs.length;
    const cy = reg.ys.reduce((s, v) => s + v, 0) / reg.ys.length;
    const rad = Math.max(60, ...reg.xs.map((x, i) => Math.hypot(x - cx, reg.ys[i] - cy))) + 50;
    const front = sides[repo] === "frontend";
    const g2 = ctx.createRadialGradient(cx, cy, rad * 0.2, cx, cy, rad);
    g2.addColorStop(0, G.overlay.halo[front ? "frontend" : "backend"]);
    g2.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g2;
    ctx.beginPath();
    ctx.arc(cx, cy, rad, 0, Math.PI * 2);
    ctx.fill();
    ctx.font = '600 10.5px "SFMono-Regular", Consolas, monospace';
    ctx.fillStyle = G.overlay.haloLabel[front ? "frontend" : "backend"];
    ctx.textAlign = "center";
    ctx.fillText(repo.split("/").pop()!.toUpperCase(), cx, cy - rad + 18);
  }
  ctx.textAlign = "start";

  // Data flow: particles travel source → target along runtime data edges.
  if (props.mode === "flow" && !hovered) {
    let i = 0;
    // Each glowing particle costs a blur: past a few hundred edges they would
    // take the frame, so many edges get one plain particle each, and only so many.
    const busy = flowEdges.length > PARTICLE_GLOW_MAX;
    const particles = busy ? 1 : 2;
    for (const [edge, attr, s, t] of flowEdges) {
      if (i >= PARTICLE_EDGES_MAX) break;
      if (r.getEdgeDisplayData(edge)?.hidden) continue;
      // Data moves from an event to its handlers, and from a route to the code serving it:
      // those edges are stored code → domain node, so their particles run backwards.
      const reverse = attr.kind === "handles" || attr.kind === "subscribes" || attr.kind === "serves_route";
      const a = viewport(reverse ? t : s), b = viewport(reverse ? s : t);
      if (!a || !b) continue;
      const color = alpha(EDGE_COLOR[attr.kind] ?? G.agg.flow, 1);
      for (let k = 0; k < particles; k++) {
        const phase = ((now / 2200) + (i * 0.137) + k * 0.5) % 1;
        const x = a.x + (b.x - a.x) * phase, y = a.y + (b.y - a.y) * phase;
        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.shadowColor = color;
        ctx.shadowBlur = busy ? 0 : 8;
        ctx.arc(x, y, 2.4, 0, Math.PI * 2);
        ctx.fill();
      }
      i++;
    }
    ctx.shadowBlur = 0;
  }

  // Ephemeral nodes (touched, but not on the indexed map): a dashed ring.
  if (ephemeral.size) {
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = G.overlay.ephemeralRing;
    ctx.lineWidth = 1.2;
    for (const id of ephemeral) {
      const p = viewport(id);
      if (!p) continue;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size + 4, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.setLineDash([]);
  }

  // The agent's trail: a fading path through the last nodes it touched.
  const pts = props.trail.map(viewport).filter((p): p is { x: number; y: number; size: number } => !!p);
  if (pts.length > 1) {
    for (let k = 1; k < pts.length; k++) {
      const a = 0.12 + 0.6 * (k / pts.length);
      ctx.strokeStyle = `rgba(${G.overlay.trail},${a.toFixed(3)})`;
      ctx.lineWidth = 1 + 2 * (k / pts.length);
      ctx.setLineDash([4, 5]);
      ctx.lineDashOffset = -now / 40;
      ctx.beginPath();
      ctx.moveTo(pts[k - 1].x, pts[k - 1].y);
      ctx.lineTo(pts[k].x, pts[k].y);
      ctx.stroke();
    }
    ctx.setLineDash([]);
  }
  // Drag to pull: what the grabbed node is carrying.
  if (pull) {
    const p = viewport(pull.node);
    if (p) {
      const text = `pulling ${pull.direct} direct · ${pull.indirect} indirect`;
      ctx.font = '600 11px "SFMono-Regular", Consolas, monospace';
      const w = ctx.measureText(text).width + 18;
      const bx = p.x + p.size + 12, by = p.y - 30;
      ctx.fillStyle = G.overlay.badgeBg;
      ctx.beginPath();
      ctx.roundRect(bx, by, w, 22, 11);
      ctx.fill();
      ctx.fillStyle = G.overlay.badgeText;
      ctx.fillText(text, bx + 9, by + 15);
      ctx.beginPath();
      ctx.strokeStyle = G.overlay.pullRing;
      ctx.lineWidth = 2;
      ctx.arc(p.x, p.y, p.size + 6 + 2 * Math.sin(now / 180), 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  // "Now": a breathing ring on the node the agent touched last.
  const head = pts[pts.length - 1];
  if (head) {
    const t = (now % 1600) / 1600;
    ctx.beginPath();
    ctx.strokeStyle = `rgba(${G.overlay.head},${(1 - t).toFixed(3)})`;
    ctx.lineWidth = 2.5;
    ctx.arc(head.x, head.y, head.size + 4 + t * 22, 0, Math.PI * 2);
    ctx.stroke();
  }
  raf = requestAnimationFrame(drawOverlay);
}

function resizeOverlay() {
  const c = overlay.value, h = host.value;
  if (!c || !h) return;
  const dpr = window.devicePixelRatio || 1;
  c.width = h.clientWidth * dpr;
  c.height = h.clientHeight * dpr;
  c.style.width = `${h.clientWidth}px`;
  c.style.height = `${h.clientHeight}px`;
}

function startPull(node: string) {
  const g = graph!;
  const children = new Set(g.outEdges(node).filter((e) => g.getEdgeAttribute(e, "kind") === "contains").map((e) => g.target(e)));
  const parents = new Set(g.inEdges(node).filter((e) => g.getEdgeAttribute(e, "kind") === "contains").map((e) => g.source(e)));
  // Connections are real edges (calls, imports, events, routes, roll-ups), not containment.
  const linked = (n: string) => {
    const out = new Set<string>();
    g.forEachEdge(n, (_e, a, src, tgt) => { const o = src === n ? tgt : src; if (a.kind !== "contains" && passes(o)) out.add(o); });
    return out;
  };
  const ring1 = new Set([...linked(node)].filter((n) => n !== node && !parents.has(n)).slice(0, PULL_MAX.direct));
  const ring2 = new Set<string>();
  for (const n of ring1) {
    for (const m of linked(n)) {
      if (m !== node && !ring1.has(m) && !parents.has(m) && !children.has(m)) ring2.add(m);
      if (ring2.size >= PULL_MAX.indirect) break;
    }
    if (ring2.size >= PULL_MAX.indirect) break;
  }
  const bodies = new Map<string, Body>();
  const r = renderer.value;
  // Only what is on screen moves (direct connections are always shown while
  // pulling): a hidden node keeps its place, so nothing needs restoring.
  const shown = (n: string) => !r?.getNodeDisplayData(n)?.hidden;
  const add = (n: string, w: number, always = false) => {
    if (bodies.has(n) || (!always && !shown(n))) return;
    const x = g.getNodeAttribute(n, "x") as number, y = g.getNodeAttribute(n, "y") as number;
    bodies.set(n, { x0: x, y0: y, x, y, vx: 0, vy: 0, w });
  };
  add(node, 1, true);
  children.forEach((n) => add(n, PULL_W.child));
  ring1.forEach((n) => add(n, PULL_W.direct, true));
  ring2.forEach((n) => add(n, PULL_W.indirect));
  pull = { node, bodies, ring1, ring2, dx: 0, dy: 0, held: true, direct: ring1.size, indirect: ring2.size };
  hovered = null;
  hoverNeighbors = new Set();
  cancelAnimationFrame(pullRaf);
  pullRaf = requestAnimationFrame(stepPull);
}

// One frame of the springs: each body chases its target (its origin plus its
// share of the drag while held, its origin once released).
function stepPull() {
  const g = graph, p = pull;
  if (!g || !p) return;
  let moving = false;
  for (const [n, b] of p.bodies) {
    if (n === p.node && p.held) continue; // the grabbed node sits under the cursor
    const tx = b.x0 + (p.held ? p.dx * b.w : 0), ty = b.y0 + (p.held ? p.dy * b.w : 0);
    b.vx = (b.vx + (tx - b.x) * SPRING_K) * SPRING_DAMP;
    b.vy = (b.vy + (ty - b.y) * SPRING_K) * SPRING_DAMP;
    const step = Math.abs(b.vx) + Math.abs(b.vy);
    b.x += b.vx; b.y += b.vy;
    if (step + Math.abs(tx - b.x) + Math.abs(ty - b.y) > 1e-3) moving = true;
    if (step > 1e-4) g.mergeNodeAttributes(n, { x: b.x, y: b.y }); // a settled body costs nothing
  }
  if (p.held || moving) { pullRaf = requestAnimationFrame(stepPull); return; }
  // Settled after a release: exact origins, normal view.
  for (const [n, b] of p.bodies) g.mergeNodeAttributes(n, { x: b.x0, y: b.y0 });
  endPull();
}

function endPull() {
  pull = null;
  cancelAnimationFrame(pullRaf);
  renderer.value?.setCustomBBox(null);
  renderer.value?.refresh({ skipIndexation: true });
}

function releasePull(keep: boolean) {
  const p = pull;
  if (!p) return;
  if (keep) {
    // Shift-release: the new positions become the layout (until the map reloads).
    endPull();
    return;
  }
  p.held = false;
  const b = p.bodies.get(p.node);
  if (b) { b.vx = 0; b.vy = 0; }
}

function wireDrag(r: Sigma) {
  r.on("downNode", ({ node }) => {
    if (props.compact) return;
    justDragged = false;
    // Keep the frame fixed while nodes move, so nothing rescales under the cursor.
    if (!r.getCustomBBox()) r.setCustomBBox(r.getBBox());
    startPull(node);
    r.refresh({ skipIndexation: true });
  });
  r.on("moveBody", ({ event }) => {
    if (!pull?.held || !graph) return;
    const pos = r.viewportToGraph(event);
    const b = pull.bodies.get(pull.node)!;
    pull.dx = pos.x - b.x0; pull.dy = pos.y - b.y0;
    b.x = pos.x; b.y = pos.y;
    graph.mergeNodeAttributes(pull.node, { x: pos.x, y: pos.y });
    if (Math.abs(pull.dx) + Math.abs(pull.dy) > 0) justDragged = true;
    event.preventSigmaDefault();
    event.original.preventDefault();
    event.original.stopPropagation();
  });
  const up = (e: { event: { original: MouseEvent | TouchEvent } }) => {
    if (!pull?.held) return;
    const keep = "shiftKey" in e.event.original && e.event.original.shiftKey;
    if (!justDragged) { endPull(); return; } // a plain click: select as usual
    releasePull(keep);
  };
  r.on("upNode", up);
  r.on("upStage", up);
}

function mount() {
  cancelAnimationFrame(pullRaf);
  pull = null;
  renderer.value?.kill();
  renderer.value = null;
  if (!host.value || !props.snapshot) return;
  graph = build(props.snapshot);
  addExtraNodes(props.extraNodes ?? []);
  let r: Sigma;
  try {
    r = new Sigma(graph, host.value, {
    renderLabels: true,
    labelFont: 'Inter, "Segoe UI", system-ui, sans-serif',
    labelSize: 12,
    labelWeight: "500",
    labelColor: { attribute: "labelColor", color: G.label },
    // A run's map names more of what it shows; the label grid still prevents overlaps.
    labelDensity: props.run ? 1 : 0.6,
    labelGridCellSize: props.run ? 80 : 100,
    labelRenderedSizeThreshold: props.compact ? 9 : props.run ? 3 : 5,
    zIndex: true,
    stagePadding: props.compact ? 24 : 56, // room for the legend and zoom chips
    minCameraRatio: 0.02,
    maxCameraRatio: 3,
    hideEdgesOnMove: false,
    defaultEdgeType: "line",
    nodeReducer,
    edgeReducer,
    defaultDrawNodeHover: drawHover,
    });
  } catch (e) {
    // No WebGL (blocked by policy, GPU blocklisted, very old browser).
    webglError.value = (e as Error)?.message ?? "WebGL unavailable";
    return;
  }
  webglError.value = "";
  r.on("enterNode", ({ node }) => {
    if (pull) return;
    hovered = node;
    hoverNeighbors = new Set(graph!.neighbors(node).filter(passes));
    hoverLinked = new Set();
    graph!.forEachEdge(node, (_e, a, src, tgt) => { const o = src === node ? tgt : src; if (a.kind !== "contains" && passes(o)) hoverLinked.add(o); });
    emit("hover", node);
    r.refresh({ skipIndexation: true });
  });
  r.on("leaveNode", () => {
    if (pull) return;
    hovered = null;
    hoverNeighbors = new Set();
    hoverLinked = new Set();
    emit("hover", null);
    r.refresh({ skipIndexation: true });
  });
  r.on("clickNode", ({ node }) => {
    if (justDragged) { justDragged = false; return; } // the end of a drag is not a selection
    emit("select", node);
  });
  wireDrag(r);
  r.on("clickStage", () => emit("select", null));
  // Reducers only re-run on refresh, not on camera moves: re-reduce when the
  // zoom crosses a semantic band so levels and roll-ups follow the zoom.
  let lastBand = band(r.getCamera().getState().ratio);
  emit("detail", lastBand);
  ratioNow = r.getCamera().getState().ratio;
  r.getCamera().on("updated", (s) => {
    ratioNow = s.ratio;
    zoomLevel.value = s.ratio;
    const b = band(s.ratio);
    if (b !== lastBand) {
      lastBand = b;
      emit("detail", b);
      r.refresh({ skipIndexation: true });
    }
  });
  renderer.value = r;
  if (import.meta.env.DEV) (window as unknown as Record<string, unknown>).__codemap = { graph, renderer: r };
  resizeOverlay();
  // Frame what the view is about from the start, not only when the focus changes later.
  if (props.focusIds?.length) {
    const ids = [...props.focusIds];
    requestAnimationFrame(() => focusOn(ids));
  }
}

function focusOn(ids: string[]) {
  const r = renderer.value;
  if (!r || !graph || !ids.length) return;
  const pts = ids.filter((id) => graph!.hasNode(id) && passes(id)).map((id) => r.getNodeDisplayData(id)).filter(Boolean) as { x: number; y: number }[];
  if (!pts.length) return;
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2, cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  const span = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
  const width = host.value?.clientWidth || 1;
  const visible = Math.max(0.4, (width - props.insetRight) / width);
  const ratio = Math.max(0.08, Math.min(1, (span * 1.6) / visible || 0.25));
  // Shift the centre right by half the covered width so the subject sits in the open area.
  const shift = (props.insetRight / 2 / width) * ratio;
  void r.getCamera().animate({ x: cx + shift, y: cy, ratio }, { duration: 700 });
}

// The granularity control: fly the camera to a detail stop, towards the
// selection when diving in.
function setDetail(level: number) {
  const r = renderer.value;
  if (!r) return;
  const b = Math.max(0, Math.min(DETAIL_RATIO.length - 1, Math.round(level)));
  const cam = r.getCamera();
  const st = cam.getState();
  let { x, y } = st;
  const sel = props.selected && graph?.hasNode(props.selected) ? r.getNodeDisplayData(props.selected) : null;
  if (sel && b > band(st.ratio)) ({ x, y } = sel);
  const still = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  void cam.animate({ x, y, ratio: DETAIL_RATIO[b] }, { duration: still ? 0 : 600 });
}

defineExpose({
  focusOn,
  setDetail,
  zoomIn: () => void renderer.value?.getCamera().animatedZoom({ duration: 300 }),
  zoomOut: () => void renderer.value?.getCamera().animatedUnzoom({ duration: 300 }),
  reset: () => void renderer.value?.getCamera().animatedReset({ duration: 500 }),
  hasNode: (id: string) => !!graph?.hasNode(id),
});

watch(() => props.snapshot, () => mount());
// The palette changed (dark ↔ light): colours are baked into the graph's attributes.
const { theme } = useTheme();
watch(theme, () => mount());
watch(() => props.extraNodes, (nodes) => {
  if (addExtraNodes(nodes ?? [])) renderer.value?.refresh();
});
watch(() => props.selected, (id) => {
  selectedNeighbors = id && graph?.hasNode(id) ? new Set(graph.neighbors(id).filter(passes)) : new Set();
});
// The filter changed: fold again, roll up again, and drop any hover or pull
// that may hold nodes it now hides.
// Detached while it changes: sigma answers every single drop and add with an
// update of its own, and a roll-up rebuild is thousands of them (seconds of
// frozen page on a large workspace). Reattached, it indexes the graph once.
const DETACHED = new Graph();
watch(() => [props.visible, props.groups], () => {
  const r = renderer.value;
  if (!graph || !r) return;
  if (pull) endPull();
  hovered = null;
  hoverNeighbors = new Set();
  hoverLinked = new Set();
  r.setGraph(DETACHED);
  applyGroups(graph);
  rebuildAggregates(graph);
  indexOverlay(graph);
  if (props.selected && graph.hasNode(props.selected)) selectedNeighbors = new Set(graph.neighbors(props.selected).filter(passes));
  r.setGraph(graph);
});
watch(() => [props.mode, props.activity, props.blast, props.footprint, props.selected, props.trail], () => renderer.value?.refresh({ skipIndexation: true }), { deep: false });
watch(() => props.focusIds, (ids) => { if (ids?.length) focusOn(ids); });

onMounted(() => {
  mount();
  raf = requestAnimationFrame(drawOverlay);
  resizeObs = new ResizeObserver(() => { resizeOverlay(); renderer.value?.refresh(); });
  if (host.value) resizeObs.observe(host.value);
});
onBeforeUnmount(() => {
  cancelAnimationFrame(raf);
  cancelAnimationFrame(pullRaf);
  resizeObs?.disconnect();
  renderer.value?.kill();
});
</script>

<template>
  <div class="codemap" :class="{ compact }">
    <div ref="host" class="canvas" />
    <canvas ref="overlay" class="overlay" aria-hidden="true" />
    <div v-if="!snapshot" class="empty"><slot name="empty"><span class="muted">Loading the code map…</span></slot></div>
    <div v-else-if="webglError" class="empty">
      <div class="nogl">
        <p class="serif">This browser can't draw the map.</p>
        <p class="muted">The code map needs WebGL, which is turned off or blocked here. Try Chrome, Edge or Firefox with hardware acceleration on.</p>
      </div>
    </div>
    <div v-if="!compact" class="zoom mono" aria-hidden="true">
      {{ DETAIL_LABELS[band(zoomLevel)].toLowerCase() }} · drag a node to pull its connections · shift-release keeps it
    </div>
  </div>
</template>

<style scoped>
.codemap { position: relative; width: 100%; height: 100%; min-height: 240px; border-radius: inherit; overflow: hidden;
  background:
    radial-gradient(circle at 20% 30%, color-mix(in srgb, var(--brand) 6%, transparent), transparent 45%),
    radial-gradient(circle at 80% 70%, color-mix(in srgb, var(--secondary) 6%, transparent), transparent 45%),
    var(--bg-sunk); }
.canvas { position: absolute; inset: 0; }
.overlay { position: absolute; inset: 0; pointer-events: none; }
.empty { position: absolute; inset: 0; display: grid; place-items: center; }
.muted { color: var(--muted); font-size: var(--text-sm); }
.nogl { max-width: 380px; text-align: center; }
.nogl .serif { font-size: var(--text-lg); margin: 0 0 var(--space-2); font-weight: 600; }
.zoom { position: absolute; left: 12px; bottom: 10px; font-size: var(--text-xs); letter-spacing: 0; text-transform: none; color: var(--faint); pointer-events: none; }
/* Phones: no Shift to release with, and the legend needs the bottom edge. */
@media (max-width: 640px) { .zoom { display: none; } }
</style>
